import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, interval, of, Subject } from 'rxjs';
import { map, takeUntil, tap, catchError, filter, take, switchMap, startWith } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { ContextService } from './context.service';
import { HttpService } from './http.service';
import { ProgressService } from './progress.service';
import { Role, Seat, UserInfo } from './auth.types';
@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private destroy$ = new Subject<void>();

  chromeToken$ = new BehaviorSubject<UserInfo | null | undefined>(undefined); // undefined = no chrome app, null = no auth token

  private __userInfo$ = new BehaviorSubject<UserInfo | null | undefined>(undefined); // undefined = no chrome app, null = no auth token
  userInfo$ = this.__userInfo$.pipe(
    filter(<T>(value: T | null | undefined): value is T | null => value !== undefined)
  );

  constructor(
    private cache: CacheService,
    private context: ContextService,
    private http: HttpService,
    private progress: ProgressService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
   
    interval(3600000).pipe(
      this.isInIframe ? tap() : startWith(0),
      switchMap(() => this.loginWithToken()),
      takeUntil(this.destroy$)
    ).subscribe();

  }

  private loginWithToken() {
    if (this.userInfo)
      return of(this.userInfo);

    return this.checkSession().pipe(
      map(response => response.data.userInfo),
      catchError(() => of(undefined)),
      tap(userInfo => this.setUserInfo(userInfo)),
      take(1),
      takeUntil(this.destroy$)
    );
  }

  setUserInfo(value: UserInfo | null | undefined) {
    this.userInfo = value || undefined;
    return of(this.userInfo);
  }

  checkSession() {
    return this.http.get<any>({
      path: 'auth/me',
      withCredentials: true
    });
  }

  get userInfo() {
    return this.__userInfo$.value;
  }

  set userInfo(value) {
    this.__userInfo$.next(value || null);

    // if (value?.socket) {
    //   this.socket.joinRoom(value.socket);
    // } else {
    //   this.socket.leaveRoom();
    // }
  }

  private isLoggingOut = false;
  logout(emit = true) {
    if (this.isLoggingOut) return;

    localStorage.setItem('proRankAuth', '');
    localStorage.setItem('authCookie', '');
    this.isLoggingOut = true;

    //this.socket.leaveRoom();

    const unsetSession = () => {
      this.userInfo = undefined;

      void this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } });

      this.context.clear();
      this.cache.clear();
      this.progress.reset();

      this.isLoggingOut = false;
    };

    if (emit) {
      this.http.get({ path: 'auth/logout', withCredentials: true }).pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        unsetSession();
      });
    } else {
      unsetSession();
    }
  }


  get name() {
    return this.userInfo ? `${this.userInfo.firstName} ${this.userInfo.lastName}` : null;
  }

  get isProRank() {
    return this.userInfo?.recruiterId === 1;
  }

  get recruiterId() {
    return this.userInfo?.recruiterId;
  }

  get isAdmin() {
    return Object.keys(this.roles).length > 0;
  }

  get roles(): Record<Role, boolean> {
    return Object.keys(this.userInfo?.roles || {}).map(x => x as Role).map(key => ({ [key]: this.userInfo?.roles[key] === 1 })).reduce((a, x) => ({ ...a, ...x }), {}) as any;
  }

  get seats(): Record<Seat, boolean> {
    return Object.keys(this.userInfo?.seats || {}).map(x => x as Seat).map(key => ({ [key]: this.userInfo?.seats[key] === 1 })).reduce((a, x) => ({ ...a, ...x }), {}) as any;
  }

  get isAuthenticated() {
    return !!this.userInfo;
  }

  get landingPage() {
    if (!this.isAuthenticated) {

      return this.router.createUrlTree(['login']).toString();
    } else if (this.seats.coreApp) {
      return this.router.createUrlTree(['jobs']).toString();
    } else if (this.isAdmin) {
      return this.router.createUrlTree(['admin']).toString();
    }

    return this.router.createUrlTree(['profile', 'settings']).toString();
  }

  get isInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }
  checkIframSession(params: Params) {
    params.sourceId = (
      {
        salesforce: 1009,
        bullhorn: 1013
      } as any
    )[params.source];
    
    return this.http.get<any>({
      path: 'iframe/me',
      params
    }).pipe(
      map(response => response.data?.userInfo),
      catchError(() => of(undefined)),
      tap(userInfo => this.setUserInfo(userInfo)),
      take(1),
      takeUntil(this.destroy$)
    );
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}